import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography } from '@mui/material';
import { EditDivisionDialogProps } from '../../utils/DataTypes';
import { useDataProvider } from 'react-admin';
import { toast } from 'react-toastify';

const EditDivisionDialog: React.FC<EditDivisionDialogProps> = ({
  isOpen,
  formValues,
  originalValues,
  setFormValues,
  onClose,
  onSave,
}) => {
  const dataProvider = useDataProvider();
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setErrorMessage(null);
    }
  }, [isOpen, formValues.division_number, formValues.division_name]);

  const handleRestore = () => {
    setFormValues(originalValues);
    setErrorMessage(null);
    toast.info('Changes discarded. Values restored to original.');
  };

  const handleValidationAndSave = async () => {
    setErrorMessage(null);

    const cleanNumber = formValues.division_number?.trim() || '';
    const cleanName = formValues.division_name?.trim() || '';

    if (!cleanNumber || !cleanName) {
      setErrorMessage('Division Number and Name are required.');
      return;
    }

    if (cleanNumber === originalValues.division_number.trim()) {
      await onSave();
      return;
    }

    setIsValidating(true);
    try {
      const { data: duplicates } = await dataProvider.getList('ValidateDivisions', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          billing_client_number: formValues.billing_client_number,
          division_numbers: [cleanNumber],
        },
      });

      if (duplicates.length > 0) {
        setErrorMessage(`Division ${cleanNumber} is already used for this client.`);
        setIsValidating(false);
        return;
      }

      await onSave();
    } catch (err) {
      toast.error('Validation failed.');
    } finally {
      setIsValidating(false);
    }
  };

  const hasChanges = JSON.stringify(formValues) !== JSON.stringify(originalValues);

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Division Crosswalk</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Box display="flex" gap={2} alignItems="center" mb={3}>
            <TextField label="Client Name" value={formValues.billing_client_name} fullWidth disabled />
            <TextField
              label="Client Number"
              value={formValues.billing_client_number || ''}
              disabled
              sx={{ width: '200px' }}
            />
          </Box>

          <TextField
            label="Division Number"
            value={formValues.division_number}
            error={!!errorMessage && !formValues.division_number?.trim()}
            onChange={(e) => {
              const val = e.target.value;
              setFormValues((prev: any) => ({
                ...prev,
                division_number: val,
                division_display_number:
                  prev.division_display_number === prev.division_number ? val : prev.division_display_number,
              }));
            }}
            fullWidth
            required
            variant="outlined"
          />
          <TextField
            label="Display Number"
            value={formValues.division_display_number}
            onChange={(e) => setFormValues((prev: any) => ({ ...prev, division_display_number: e.target.value }))}
            fullWidth
            variant="outlined"
          />
          <TextField
            label="Division Name"
            value={formValues.division_name}
            error={!!errorMessage && !formValues.division_name?.trim()}
            onChange={(e) => setFormValues((prev: any) => ({ ...prev, division_name: e.target.value }))}
            fullWidth
            required
            variant="outlined"
          />

          {errorMessage && (
            <Box p={1.5} sx={{ backgroundColor: '#fff5f5', border: '1px solid #ffcccc', borderRadius: 1 }}>
              <Typography color="error" variant="body2" sx={{ fontWeight: 500 }}>
                ⚠️ {errorMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button onClick={handleRestore} color="inherit" disabled={!hasChanges || isValidating}>
          Restore Original
        </Button>
        <Box>
          <Button onClick={onClose} disabled={isValidating} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleValidationAndSave}
            color="primary"
            variant="contained"
            disabled={!hasChanges || isValidating}
          >
            {isValidating ? 'Validating...' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditDivisionDialog;
